import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, XCircle, Clock, Send, ArrowUpDown, FileText, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { LANGUAGES } from "@/lib/languages";

type Article = {
  id: string;
  title: string;
  content: string | null;
  excerpt: string | null;
  status: string;
  slug: string;
  tags: string[] | null;
  category_id: string | null;
  website_id: string | null;
  language: string | null;
  featured_image_url: string | null;
  updated_at: string;
  published_at: string | null;
  categories?: { id: string; name: string } | null;
};

type Site = { id: string; name: string; domain: string | null };

type Props = {
  currentSiteId: string;
  currentSiteName: string;
};

type SortKey = "updated_at" | "title" | "relevance";

export default function ManualPublishPanel({ currentSiteId, currentSiteName }: Props) {
  const queryClient = useQueryClient();

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [languageFilter, setLanguageFilter] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortKey>("updated_at");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetSites, setTargetSites] = useState<Set<string>>(new Set([currentSiteId]));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState<{ id: string; site: string; ok: boolean; msg?: string }[]>([]);

  // Articles
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["manual-publish-articles", statusFilter],
    queryFn: async () => {
      let q = supabase
        .from("articles")
        .select("id, title, content, excerpt, status, slug, tags, category_id, website_id, language, featured_image_url, updated_at, published_at, categories(id, name)")
        .order("updated_at", { ascending: false })
        .limit(200);
      if (statusFilter !== "all") q = q.eq("status", statusFilter as any);
      const { data, error } = await q;
      if (error) throw error;
      return (data as unknown as Article[]) || [];
    },
  });

  // Categories
  const { data: categories = [] } = useQuery({
    queryKey: ["categories-all"],
    queryFn: async () => {
      const { data } = await supabase.from("categories").select("id, name").order("name");
      return data || [];
    },
  });

  // Websites for multi-target
  const { data: websites = [] } = useQuery({
    queryKey: ["websites-publish-targets"],
    queryFn: async () => {
      const { data } = await supabase
        .from("websites")
        .select("id, name, domain, technical_config")
        .order("name");
      return (data || []) as (Site & { technical_config: any })[];
    },
  });

  // Existing publications to detect duplicates per site
  const { data: existingPubs = [] } = useQuery({
    queryKey: ["manual-publish-pubs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("article_publications")
        .select("article_id, website_id, status");
      return data || [];
    },
  });

  const pubMap = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    for (const p of existingPubs) {
      if (!m.has(p.article_id)) m.set(p.article_id, new Map());
      m.get(p.article_id)!.set(p.website_id, p.status);
    }
    return m;
  }, [existingPubs]);

  // Filtering + scoring
  const getPublicationStatus = useCallback(
    (articleId: string, siteId: string) => pubMap.get(articleId)?.get(siteId),
    [pubMap],
  );
  const isPublishedToAnyTarget = useCallback(
    (articleId: string) => Array.from(targetSites).some((siteId) => getPublicationStatus(articleId, siteId) === "published"),
    [targetSites, getPublicationStatus],
  );
  const isEligible = useCallback(
    (a: Article) => isReady(a) && !isPublishedToAnyTarget(a.id),
    [isPublishedToAnyTarget],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const tokens = term.split(/\s+/).filter(Boolean);
    let list = articles.filter((a) => {
      if (categoryFilter !== "all" && a.category_id !== categoryFilter) return false;
      if (languageFilter.size > 0 && !languageFilter.has(a.language || "")) return false;
      if (isPublishedToAnyTarget(a.id)) return false;
      if (!term) return true;
      const hay = `${a.title} ${a.excerpt || ""} ${(a.tags || []).join(" ")}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });

    // Relevance score
    const scored = list.map((a) => {
      let score = 0;
      if (term) {
        const title = a.title.toLowerCase();
        for (const t of tokens) {
          if (title.includes(t)) score += 5;
          if ((a.tags || []).some((tag) => tag.toLowerCase().includes(t))) score += 3;
          if ((a.excerpt || "").toLowerCase().includes(t)) score += 1;
        }
      }
      // Quality bonus
      if (a.featured_image_url) score += 2;
      if ((a.content || "").length > 500) score += 2;
      if (a.excerpt) score += 1;
      return { ...a, _score: score };
    });

    if (sortBy === "title") scored.sort((a, b) => a.title.localeCompare(b.title));
    else if (sortBy === "relevance") scored.sort((a, b) => b._score - a._score);
    else scored.sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));

    return scored;
    // pubMap + targetSites flow through isPublishedToAnyTarget; listing them
    // again is redundant (eslint flags as "unnecessary dependency").
  }, [articles, search, categoryFilter, languageFilter, sortBy, isPublishedToAnyTarget]);

  const toggleLanguageFilter = (code: string) => {
    setLanguageFilter((prev) => {
      const n = new Set(prev);
      n.has(code) ? n.delete(code) : n.add(code);
      return n;
    });
  };

  const toggleArticle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    const eligibleIds = filtered.filter(isEligible).map((a) => a.id);
    if (eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id))) setSelected(new Set());
    else setSelected(new Set(eligibleIds));
  };

  const toggleSite = (id: string) => {
    setTargetSites((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // Quality check
  const isReady = (a: Article) => !!a.title && !!a.content && a.content.length > 100;

  const readyCount = useMemo(() => {
    return filtered.filter((a) => selected.has(a.id) && isEligible(a)).length;
    // pubMap + targetSites flow through isEligible; listing them is redundant.
  }, [filtered, selected, isEligible]);

  const handlePublish = async () => {
    if (selected.size === 0 || targetSites.size === 0) return;
    setPublishing(true);
    setPublishResults([]);
    const results: { id: string; site: string; ok: boolean; msg?: string }[] = [];

    const articlesToPublish = filtered.filter((a) => selected.has(a.id) && isEligible(a));
    const siteList = websites.filter((s) => targetSites.has(s.id));

    for (const article of articlesToPublish) {
      for (const site of siteList) {
        // Duplicate check
        const existing = pubMap.get(article.id)?.get(site.id);
        if (existing === "published") {
          results.push({ id: article.id, site: site.name, ok: false, msg: "Already published" });
          continue;
        }
        try {
          const { data, error } = await supabase.functions.invoke("publish-to-wordpress", {
            body: { article_id: article.id, website_id: site.id, action: "publish" },
          });
          if (error) throw error;
          if ((data as any)?.error) throw new Error((data as any).error);
          results.push({ id: article.id, site: site.name, ok: true });
        } catch (e: any) {
          results.push({ id: article.id, site: site.name, ok: false, msg: e.message?.slice(0, 100) });
        }
      }
    }

    setPublishResults(results);
    setPublishing(false);
    const okCount = results.filter((r) => r.ok).length;
    if (okCount > 0) toast.success(`Published ${okCount} of ${results.length} attempts`);
    if (okCount < results.length) toast.error(`${results.length - okCount} failed — see details`);
    queryClient.invalidateQueries({ queryKey: ["manual-publish-pubs"] });
    queryClient.invalidateQueries({ queryKey: ["site-articles", currentSiteId] });
  };

  const statusColor: Record<string, string> = {
    draft: "bg-muted text-muted-foreground",
    review: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    approved: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
    published: "bg-primary/15 text-primary",
    scheduled: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    archived: "bg-muted text-muted-foreground",
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, content, tags…"
            className="pl-8 h-8 text-xs"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c: any) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <ArrowUpDown className="h-3 w-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated_at">Most recent</SelectItem>
            <SelectItem value="title">Title (A→Z)</SelectItem>
            <SelectItem value="relevance">Relevance</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 && (
            <span className="text-xs text-muted-foreground">
              {selected.size} selected · {readyCount} ready
            </span>
          )}
          <Button
            size="sm"
            disabled={selected.size === 0 || readyCount === 0}
            onClick={() => setConfirmOpen(true)}
            className="h-8 text-xs"
          >
            <Send className="h-3 w-3 mr-1.5" />
            Publish Selected
          </Button>
        </div>
      </div>

      {/* Language filter chips */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Globe className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Language:</span>
        <button
          onClick={() => setLanguageFilter(new Set())}
          className={cn(
            "rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors",
            languageFilter.size === 0
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-muted text-muted-foreground border-border hover:text-foreground"
          )}
        >
          All
        </button>
        {LANGUAGES.map((l) => {
          const active = languageFilter.has(l.code);
          return (
            <button
              key={l.code}
              onClick={() => toggleLanguageFilter(l.code)}
              className={cn(
                "rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors",
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:text-foreground"
              )}
            >
              {l.flag} {l.code.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* Article table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-3 py-2 w-8">
                  <Checkbox
                    checked={filtered.length > 0 && filtered.filter(isEligible).every((a) => selected.has(a.id))}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Article</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Category</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Quality</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr><td colSpan={6} className="p-8 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                  <FileText className="h-6 w-6 mx-auto mb-2 opacity-40" />
                  No eligible articles match your filters
                </td></tr>
              ) : filtered.map((a) => {
                const ready = isReady(a);
                const eligible = isEligible(a);
                return (
                  <tr
                    key={a.id}
                    onClick={() => eligible && toggleArticle(a.id)}
                    className={cn(
                      "transition-colors",
                      eligible ? "cursor-pointer hover:bg-accent/40" : "cursor-not-allowed opacity-60",
                      selected.has(a.id) && "bg-primary/5"
                    )}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(a.id)} disabled={!eligible} onCheckedChange={() => toggleArticle(a.id)} />
                    </td>
                    <td className="px-3 py-2 max-w-md">
                      <div className="flex items-center gap-2">
                        {a.featured_image_url && (
                          <img src={a.featured_image_url} alt="" className="h-8 w-8 rounded object-cover" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{a.title}</p>
                          {a.excerpt && <p className="text-[10px] text-muted-foreground truncate">{a.excerpt}</p>}
                        </div>
                        {!ready && <Badge variant="outline" className="text-[9px] shrink-0">Needs content</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium uppercase", statusColor[a.status] || statusColor.draft)}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{a.categories?.name || "—"}</td>
                    <td className="px-3 py-2">
                      {ready ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-3 w-3" /> Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <Clock className="h-3 w-3" /> Thin content
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{format(new Date(a.updated_at), "MMM d, HH:mm")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Publish confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Publish {selected.size} article{selected.size !== 1 ? "s" : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-foreground mb-2">Target websites</p>
              <div className="grid grid-cols-2 gap-1.5 max-h-60 overflow-y-auto rounded-md border border-border p-2">
                {websites.map((s) => {
                  const hasWp = !!(s.technical_config as any)?.wordpress?.api_url;
                  return (
                    <label
                      key={s.id}
                      className={cn(
                        "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-accent/40 transition-colors",
                        targetSites.has(s.id) && "bg-primary/10"
                      )}
                    >
                      <Checkbox
                        checked={targetSites.has(s.id)}
                        disabled={!hasWp}
                        onCheckedChange={() => toggleSite(s.id)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate">{s.name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{s.domain || "no domain"}</p>
                      </div>
                      {!hasWp && <Badge variant="outline" className="text-[9px]">Not connected</Badge>}
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Sites without connection credentials are disabled. Configure them in Deploy → Site Connection.
              </p>
            </div>

            {publishResults.length > 0 && (
              <div className="rounded-md border border-border p-3 max-h-48 overflow-y-auto space-y-1">
                <p className="text-xs font-medium mb-2">Results</p>
                {publishResults.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    {r.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <XCircle className="h-3 w-3 text-destructive" />}
                    <span className="text-muted-foreground">{r.site}</span>
                    {r.msg && <span className="text-destructive">— {r.msg}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={publishing}>
              Close
            </Button>
            <Button onClick={handlePublish} disabled={publishing || targetSites.size === 0}>
              {publishing ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Send className="h-3 w-3 mr-1.5" />}
              Publish to {targetSites.size} site{targetSites.size !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
